import { DateTime } from 'luxon';
import { Log_Meal, mealSlotKey, mealWasAddedByHand, mealWouldCountTowardDayTotal } from '@cf-shared/data-model/log-meal';

/**
 * What to report when a day's one-meal-per-slot collapse left meals out of the day's total, or
 * nothing when there is nothing to report.
 *
 * `reportKey` is what the caller remembers so the same day is not reported twice in a session;
 * `isUnresolvedProblem` picks the telemetry tier; `name` and `context` go straight to the logging
 * service.
 */
export interface DroppedMealsReport {
  reportKey: string;
  isUnresolvedProblem: boolean;
  name: string;
  context: {
    day: string;
    droppedCount: number;
    droppedIds: string;
    droppedSlots: string;
    /** Only on the time-zone report: the copies that won each slot a dropped meal belongs to. */
    keptIds?: string;
    /** Only on the time-zone report: `<slot>:<hidden offset>/<kept offset>` per pair, in minutes. */
    offsets?: string;
  };
}

/** A meal holding food was left out of the day's total — still unresolved, so problem tier. */
export const DAY_TOTAL_DUPLICATE_MEAL_SLOT = 'day-total-duplicate-meal-slot';

/** Two schedule-built copies from two time zones, both holding food — problem tier (#6879). */
export const DAY_TOTAL_DUPLICATE_MEAL_SLOT_TIME_ZONES = 'day-total-duplicate-meal-slot-time-zones';

/** Only empty copies were left out — the total did not change, so the quieter info tier. */
export const DAY_TOTAL_DUPLICATE_EMPTY_MEAL_SLOT = 'day-total-duplicate-empty-meal-slot';

/**
 * Describe the report for the meals `pickOneMealPerSlot` left out of a day.
 *
 * Two very different things reach here and they must not be reported at the same tier. The
 * collapse prefers a meal holding food over an empty placeholder, so a dropped meal only costs the
 * user something when TWO filled documents share one slot — the ordinary case drops a leftover
 * placeholder, which would have added nothing to the total anyway. So: a drop that could not change
 * the total is a completed repair and goes to the info tier, and only a drop that removes real food
 * is an unresolved problem. Deciding that on the nutrient VALUES rather than on `state` means a
 * placeholder that somehow arrived pre-filled is still counted as a real loss. See
 * design-docs/telemetry.md, "Three telemetry severity tiers".
 *
 * One food-losing shape gets its own name (#6879): two copies the schedule built on two devices in
 * different time zones, both holding food. A meal's id names the instant it was built, so two
 * devices in different zones build different ids for one slot and sync keeps both. Such a pair is
 * either two different meals (a traveller who planned the day once before the trip and again
 * after) or one meal seen twice (the #4728 client and coach copies), and nothing stored tells them
 * apart. The owner chose to measure how often it happens before picking a fix, so the day is
 * reported under that name when EVERY food-losing drop is such a pair. Any other food-losing drop
 * keeps the general name, so that report still catches every other shape.
 *
 * The key is the day plus the dropped ids, so a NEW duplicate on an already-reported day is still
 * reported. The ids are SORTED first, because the dropped list comes back in the caller's order and
 * the callers order their meals differently, so an unsorted key would let one day report once per
 * caller.
 */
export function describeDroppedDuplicateMeals(
  dayReferenceTime: DateTime,
  kept: Log_Meal[],
  dropped: Log_Meal[],
): DroppedMealsReport | undefined {
  // if nothing was left out, there is nothing to report
  if (dropped.length === 0) return undefined;

  const day = dayReferenceTime.toISODate();
  const droppedIds = dropped.map(m => m._id);
  const context: DroppedMealsReport['context'] = {
    day,
    droppedCount: dropped.length,
    droppedIds: droppedIds.join(','),
    droppedSlots: dropped.map(m => mealSlotKey(m)).join(','),
  };
  const report = (isUnresolvedProblem: boolean, name: string): DroppedMealsReport => ({
    reportKey: `${day}:${[...droppedIds].sort().join(',')}`,
    isUnresolvedProblem,
    name,
    context,
  });

  // if no dropped meal held food, the total did not change
  const losingFood = dropped.filter(mealWouldCountTowardDayTotal);
  if (losingFood.length === 0) return report(false, DAY_TOTAL_DUPLICATE_EMPTY_MEAL_SLOT);

  // pair each food-losing drop with the copy that won its slot; if any one is not a time-zone
  // pair, the day keeps the general name
  const keptBySlot = new Map(kept.map(m => [mealSlotKey(m), m]));
  const pairs = losingFood.map(hidden => ({ hidden, shown: keptBySlot.get(mealSlotKey(hidden)) }));
  if (!pairs.every(({ hidden, shown }) => !!shown && isTimeZonePair(hidden, shown))) {
    return report(true, DAY_TOTAL_DUPLICATE_MEAL_SLOT);
  }

  context.keptIds = pairs.map(p => p.shown._id).join(',');
  context.offsets = pairs.map(p => `${p.hidden.name}:${p.hidden.timeOffset_min}/${p.shown.timeOffset_min}`).join(',');
  return report(true, DAY_TOTAL_DUPLICATE_MEAL_SLOT_TIME_ZONES);
}

/** Both copies hold food, the schedule built both, and they were built in different time zones. */
function isTimeZonePair(hidden: Log_Meal, shown: Log_Meal): boolean {
  return mealWouldCountTowardDayTotal(shown)
    && !mealWasAddedByHand(hidden)
    && !mealWasAddedByHand(shown)
    && hidden.timeOffset_min !== shown.timeOffset_min;
}
